export type UserRole = 'user' | 'approver' | 'admin';

export interface User {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  is_admin: boolean;
  is_approver: boolean;
  is_active?: boolean;
  exclude_from_sync?: boolean;
  microsoft_365_email?: string;
  microsoft_365_user_id?: string;
  // Effektive Modul-Zugriffe (vom Backend berechnet: global UND Rolle/Override)
  modules?: Record<string, boolean>;
  // Admin-Ansicht: rohe Overrides {"module_key": "on"|"off"}, fehlender Key = erben
  module_overrides?: Record<string, 'on' | 'off'> | null;
  // Verknüpfter Master-Kontakt (Bauleiter-Zuordnung), admin-gepflegt
  contact_id?: number | null;
  contact_name?: string | null;
}

export interface ModuleConfig {
  module_key: string;
  is_enabled: boolean;
  role_defaults: Record<string, boolean>;
  sort_order?: number;
}

export interface AuthResponse {
  user: User;
  tokens: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
  };
}

export interface Address {
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

export interface Contact {
  id: number;
  global_uuid: string;
  
  // Name fields
  first_name?: string;
  last_name?: string;
  display_name?: string;
  title?: string;
  
  // Company
  company_name?: string;
  department?: string;
  job_title?: string;
  
  // Multiple emails
  email_address?: string;
  secondary_email?: string;
  tertiary_email?: string;
  
  // Multiple phones
  mobile_phone?: string;
  business_phone?: string;
  business_phone_2?: string;
  home_phone?: string;
  home_phone_2?: string;
  fax?: string;
  pager?: string;
  
  // Business address
  street?: string;
  city?: string;
  postal_code?: string;
  state?: string;
  country?: string;
  
  // Home address
  home_street?: string;
  home_city?: string;
  home_postal_code?: string;
  home_state?: string;
  home_country?: string;
  
  // Other address
  other_street?: string;
  other_city?: string;
  other_postal_code?: string;
  other_state?: string;
  other_country?: string;
  
  // Other fields
  business_homepage?: string;
  personal_notes?: string;
  birthday?: string;
  
  // Sync fields
  content_hash?: string;
  created_at: string;
  updated_at: string;

  // Sync origin (only set on rows that came in from a specific user's
  // Outlook via `SyncService` — used by the merge-into-existing flow
  // to know which mailbox to clean up).
  source_user_email?: string;
  ms_contact_id?: string;

  // Pending proposal
  pending_proposals_count?: number;
  pending_proposal_data?: Partial<Contact>;
}

export type ProposalStatus = 'pending' | 'approved' | 'rejected';
export type ProposalAction = 'create' | 'update' | 'delete';

export interface PotentialDuplicate {
  id: number;
  global_uuid: string;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  company_name?: string | null;
  email_address?: string | null;
  secondary_email?: string | null;
  tertiary_email?: string | null;
  mobile_phone?: string | null;
  business_phone?: string | null;
  business_phone_2?: string | null;
  home_phone?: string | null;
  home_phone_2?: string | null;
  content_hash?: string | null;
  match_score: number;
  match_reasons: string[];
}

export interface Proposal {
  id: number;
  proposed_by_user_id: number;
  proposed_by_name?: string;
  proposed_by_email?: string;
  action: ProposalAction;
  contact_id?: number;
  new_contact_data: Partial<Contact>;
  changes_summary?: string[];
  /** Master contacts that look like the same person as the proposal. */
  potential_duplicates?: PotentialDuplicate[];
  status: ProposalStatus;
  review_notes?: string;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
}

export type DetectedChangeStatus = 'detected' | 'converted_to_proposal' | 'ignored' | 'approved_direct' | 'rejected';

export interface DetectedChange {
  id: number;
  detected_from_user_id: number;
  detected_from_name?: string;
  detected_from_email?: string;
  source_contact_id?: number;
  action: ProposalAction;
  captured_data: Partial<Contact>;
  ms_graph_contact_id?: string;
  status: DetectedChangeStatus;
  detected_at: string;
  processed_at?: string;
}

export interface PaginationInfo {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: PaginationInfo;
}

export interface SyncStatus {
  recent_operations: SyncLog[];
  user_states: UserSyncState[];
  pending_changes: {
    proposals: number;
    detected_changes: number;
  };
}

export interface SyncLog {
  id: number;
  operation_type: string;
  status: string;
  target_user_email?: string;
  details?: Record<string, unknown>;
  error_message?: string;
  started_at: string;
  completed_at?: string;
}

export interface UserSyncState {
  user_id: number;
  name: string;
  email: string;
  last_push_at?: string;
  last_pull_at?: string;
  is_syncing: boolean;
  last_error?: string;
  last_error_at?: string;
}

// Calendar/Event Types
export type EventType =
  | 'company_event'
  | 'holiday'
  | 'meeting'
  | 'birthday'
  | 'arzttermin'
  | 'urlaub'
  | 'other'
  | 'privat'
  | 'physio'
  | 'krank'
  | 'kinder_kindergarten_schule'
  | 'betriebsurlaub_subunternehmer'
  | 'sonderurlaub'
  | 'baustellentermin'
  | 'privat_kfz';

export interface CalendarEvent {
  id: number;
  global_uuid: string;
  title: string;
  description?: string;
  location?: string;
  event_type: EventType;
  start_date: string;
  end_date?: string;
  is_all_day: boolean;
  timezone: string;
  is_public: boolean;
  target_departments?: string[];
  content_hash?: string;
  ms_graph_event_id?: string;
  created_by_user_id: number;
  created_by_name?: string;
  approved_by_user_id?: number;
  approved_by_name?: string;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  // Pending proposal fields (when shown in calendar before approval)
  proposal_id?: number;
  proposal_status?: string;
  proposal_action?: string;
  // Contact birthday overlay (not a real calendar event)
  contact_id?: number;
  contact_birthday?: string;
  birthday_age?: number;
}

export interface EventProposal {
  id: number;
  proposed_by_user_id: number;
  proposed_by_name?: string;
  proposed_by_email?: string;
  action: ProposalAction;
  event_id?: number;
  new_event_data: Partial<CalendarEvent>;
  existing_title?: string;
  existing_start_date?: string;
  status: ProposalStatus;
  review_notes?: string;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ImportResult {
  imported: Array<{
    proposal_id: number;
    contact: string;
    action: string;
    reason: string;
    status: string;
  }>;
  errors: Array<{
    contact: string;
    error: string;
  }>;
  total: number;
  success_count: number;
  error_count: number;
}

// Bauvorhaben (Phase 1: Stammdaten)
export type BauvorhabenStatus = 'geplant' | 'aktiv' | 'abgeschlossen' | 'abgebrochen';
export type BauvorhabenContactRole = 'bauherr' | 'gewerk' | 'sonstiges';

export interface Bauvorhaben {
  id: number;
  name: string;
  project_number?: string | null;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  status: BauvorhabenStatus;
  start_date?: string | null;
  end_date?: string | null;
  bauleiter_user_id?: number | null;
  bauleiter_name?: string | null;
  bauleiter_contact_id?: number | null;
  vertriebler_contact_id?: number | null;
  vertriebler_name?: string | null;
  bauherren_count?: number;
  gewerke_fertig_count?: number;
  gewerke_in_arbeit_count?: number;
  /** Nenner: aktive Katalog-Gewerke (global). */
  gewerke_total_count?: number;
  /** Gewichtet: fertig = 1, in_arbeit = 0,5 (0–100). */
  progress_percent?: number;
  notes?: string | null;
  created_by_user_id?: number;
  created_at: string;
  updated_at: string;
}

export interface BauvorhabenContact {
  link_id: number;
  contact_id: number;
  role: BauvorhabenContactRole;
  gewerk_label: string;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  email_address?: string | null;
  business_phone?: string | null;
}

export interface BauvorhabenDetail {
  bauvorhaben: Bauvorhaben;
  contacts: BauvorhabenContact[];
}

// PUT-Antwort: Bauvorhaben + Anzahl automatisch erzeugter Adress-Anträge
export interface BauvorhabenUpdateResult extends Bauvorhaben {
  address_proposals_created?: number;
}

// Bauvorhaben-Verknüpfung eines Kontakts (Kontakt-Detailseite, lesend)
export type ContactBauvorhabenRole = BauvorhabenContactRole | 'vertriebler';

export interface ContactBauvorhabenLink {
  id: number;
  name: string;
  status: BauvorhabenStatus;
  role: ContactBauvorhabenRole;
  /** '' bei bauherr/sonstiges/vertriebler */
  gewerk_label: string;
}

// Gewerk-Katalog (Bauvorhaben Phase 3)
export type GewerkStatus = 'zu_erledigen' | 'in_arbeit' | 'fertig';

export interface Gewerk {
  id: number;
  name: string;
  sort_order: number;
  is_active: boolean;
}

export interface GewerkPhase {
  id: number;
  name: string;
  sort_order: number;
  gewerke: Gewerk[];
}

export interface GewerkContactLink {
  link_id: number;
  contact_id: number;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  email_address?: string | null;
  business_phone?: string | null;
}

// Akkordeon-Payload je Bauvorhaben: aktive Gewerke mit Status + Kontakten
export interface AccordionGewerk extends Gewerk {
  status: GewerkStatus;
  contacts: GewerkContactLink[];
}

export interface AccordionPhase extends GewerkPhase {
  gewerke: AccordionGewerk[];
}

export interface AccordionPayload {
  phases: AccordionPhase[];
}

// ============================================================
// Dokumente (Modul 'dokumente'): pro Bauvorhaben strukturierte
// Ablage mit festen Kategorien — Ersatz für die Netzlaufwerk-
// Ordner. Bewusst OHNE Rechnungen (die bleiben im Rechnungen-
// Modul).
// ============================================================

export type DocumentCategory =
  | 'schriftwechsel'
  | 'bauamt'
  | 'architektur'
  | 'bauherr'
  | 'gewerke'
  | 'bilder'
  | 'abnahme'
  | 'abrechnung'
  | 'baudurchsprache'
  | 'medien'
  | 'sonstiges';

/** Eine Row je Datei; Multi-Upload = N Rows mit gemeinsamem Titel. */
export interface BauvorhabenDocument {
  id: number;
  bauvorhaben_id: number;
  bauvorhaben_name?: string | null;
  category: DocumentCategory;
  title: string | null;
  file_path: string | null;
  original_name: string | null;
  file_size: number | null;
  file_mime: string | null;
  gewerk_id: number | null;
  gewerk_name?: string | null;
  taken_at: string | null; // DATE (YYYY-MM-DD)
  uploaded_by_user_id: number | null;
  uploaded_by_name?: string | null;
  created_at: string;
  updated_at: string;
}

/** Summary der Per-BV-Variante (GET dokumente?bauvorhaben_id=). */
export interface DokumenteSummary {
  total_count: number;
  image_count: number;
  counts_by_category: Record<DocumentCategory, number>;
}

/** Payload der Per-BV-Variante (konsumiert von der BV-Karte). */
export interface BauvorhabenDokumente {
  documents: BauvorhabenDocument[];
  summary: DokumenteSummary;
}

/** Upload-Response: partial success wie Rechnungen-Anlagen. */
export interface DokumenteUploadResult {
  documents: BauvorhabenDocument[];
  errors: { name: string; error: string }[];
}

export interface DokumentePagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

/** de-DE-Labels der festen Kategorien (Reihenfolge = Anzeige-Reihenfolge). */
export const documentCategoryLabels: Record<DocumentCategory, string> = {
  schriftwechsel: 'Schriftwechsel',
  bauamt: 'Bauamt',
  architektur: 'Architektur',
  bauherr: 'Bauherr',
  gewerke: 'Gewerke',
  bilder: 'Bilder',
  abnahme: 'Abnahme',
  abrechnung: 'Abrechnung',
  baudurchsprache: 'Baudurchsprache',
  medien: 'Medien',
  sonstiges: 'Sonstiges',
};

// ============================================================
// Rechnungen (Modul 'rechnungen', Phase 1)
// Eingangs-/Ausgangsrechnungen teilen sich ein Entität (direction).
// Status-Workflow siehe InvoiceController::TRANSITIONS.
// ============================================================

export type InvoiceDirection = 'eingang' | 'ausgang';

export type InvoiceStatus =
  | 'erfasst'
  | 'formal_geprueft'
  | 'freigegeben'
  | 'korrektur_erforderlich'
  | 'abgelehnt'
  | 'an_lexware_uebergeben';

export type PaymentStatus = 'offen' | 'bezahlt';

export type InvoiceTransitionAction =
  | 'formal_check'
  | 'approve'
  | 'request_correction'
  | 'reject'
  | 'reopen_review'
  | 'mark_exported'
  | 'reopen_lexware'
  | 'mark_paid'
  | 'mark_unpaid';

/** Beträge kommen als String aus PDO (DECIMAL) — Frontend formatiert. */
export interface Invoice {
  id: number;
  direction: InvoiceDirection;
  invoice_number: string;
  status: InvoiceStatus;
  payment_status: PaymentStatus;
  /** DECIMAL → string (de-DE-Formatierung im Frontend) */
  amount_gross: string;
  /** Grid-Spalten (seit Bulk-Endpoint auch in der Liste) — Liste = String
   *  (DECIMAL), Detail (show()) liefert Zahlen; NULL wenn nicht gesetzt. */
  amount_net?: string | number | null;
  amount_tax?: string | number | null;
  tax_rate_percent?: string | number | null;
  invoice_date: string;
  due_date?: string | null;
  description?: string | null;
  /** Dokumentation/Bericht (umsgenutztes Notizen-Feld, Phase 3). */
  notes?: string | null;
  currency: string;
  file_path?: string | null;
  file_original_name?: string | null;
  file_size?: number | null;
  file_mime?: string | null;
  bauvorhaben_id: number | null;
  bauvorhaben_name?: string | null;
  project_number?: string | null;
  /** Für can_review — wird serverseitig berechnet, das UI dupliziert die Rollenlogik nicht. */
  bauleiter_user_id: number | null;
  gewerk_id: number | null;
  gewerk_name?: string | null;
  contact_id: number | null;
  contact_name?: string | null;
  created_by_user_id: number;
  created_by_name?: string | null;
  lexware_exported_at?: string | null;
  created_at: string;
  updated_at: string;
  /** Phase 3: Anzahl Anlagen (invoice_files); has_file = attachment_count > 0. */
  attachment_count?: number;
  has_file: boolean;
  can_review: boolean;
}

/** Payload für create/update — Beträge als Zahlen (NULL = nicht gesetzt). */
export interface InvoiceWritePayload {
  direction?: InvoiceDirection;
  invoice_number?: string;
  contact_id?: number | null;
  bauvorhaben_id?: number | null;
  gewerk_id?: number | null;
  amount_net?: number | null;
  amount_tax?: number | null;
  amount_gross?: number;
  tax_rate_percent?: number | null;
  invoice_date?: string;
  due_date?: string | null;
  description?: string | null;
  notes?: string | null;
}

export interface InvoiceStatusLogEntry {
  id: number;
  from_status: string | null;
  to_status: string;
  action: string;
  comment?: string | null;
  created_at: string;
  user_name?: string | null;
}

export interface InvoiceComment {
  id: number;
  comment: string;
  is_internal: boolean;
  created_at: string;
  user_id: number;
  user_name?: string | null;
  user_role?: string | null;
}

export interface InvoiceDetail extends Invoice {
  notes?: string | null;
  uploaded_by_user_id: number | null;
  uploaded_by_name?: string | null;
  source: 'manuell' | 'email';
  paid_at?: string | null;
  lexware_exported_by_name?: string | null;
  status_log: InvoiceStatusLogEntry[];
  comments: InvoiceComment[];
}

/** Anlage (invoice_files, Phase 3) — mehrere Dateien je Rechnung. */
export interface InvoiceFile {
  id: number;
  invoice_id: number;
  file_path: string | null;
  original_name: string | null;
  file_size: number | null;
  file_mime: string | null;
  is_primary: boolean;
  uploaded_by_user_id: number | null;
  uploaded_by_name?: string | null;
  created_at: string;
}

export interface InvoiceDetailResponse {
  invoice: InvoiceDetail;
  status_log: InvoiceStatusLogEntry[];
  comments: InvoiceComment[];
  attachments?: InvoiceFile[];
}

/** Ergebnis des Anlagen-Uploads (POST rechnungen/{id}/files) — partial success. */
export interface InvoiceFilesResult {
  files: InvoiceFile[];
  errors: Array<{ name: string; error: string }>;
}

/** Finanz-Summen je Bauvorhaben (BV-Detailkarte); Werte als Strings (DECIMAL). */
export interface BauvorhabenInvoiceSummary {
  sum_offen: string;
  sum_freigegeben: string;
  sum_gesamt: string;
  count_offen: number;
  count_offen_overdue: number;
  total_count: number;
}

export interface BauvorhabenRechnungen {
  invoices: Invoice[];
  summary: BauvorhabenInvoiceSummary;
}

/** Ergebnis des Bulk-Imports (POST rechnungen/bulk) — partial success. */
export interface InvoiceBulkResult {
  created: number;
  skipped: number;
  errors: Array<{ row: number; invoice_number: string; error: string }>;
}

// ============================================================
// Dashboard (GET dashboard — Aggregat „Offene Aufgaben" je Rolle)
// ============================================================

/** Admin-Sicht (Buchhaltung): Zähler je Status mit Handlungsbedarf. */
export interface DashboardAdminCounts {
  erfasst: number;
  korrektur_erforderlich: number;
  freigegeben: number;
  ueberfaellig: number;
}

/** Bauleiter-Sicht: Rechnungen zur Freigabe auf eigenen Bauvorhaben. */
export interface DashboardFreigabeItem {
  id: number;
  invoice_number: string;
  direction: InvoiceDirection;
  amount_gross: string;
  invoice_date: string;
  bauvorhaben_name: string;
}

export interface DashboardInvoicesAdmin {
  scope: 'admin';
  counts: DashboardAdminCounts;
}

export interface DashboardInvoicesBauleiter {
  scope: 'bauleiter';
  zur_freigabe_count: number;
  zur_freigabe: DashboardFreigabeItem[];
}

export type DashboardInvoices = DashboardInvoicesAdmin | DashboardInvoicesBauleiter;

/** Pendende Anträge (approver+). */
export interface DashboardAntraege {
  pending_contacts: number;
  pending_events: number;
}

export interface DashboardEvent {
  id: number;
  title: string;
  event_type: EventType;
  start_date: string;
  end_date?: string | null;
  is_all_day: boolean;
}

export interface DashboardData {
  invoices?: DashboardInvoices;
  antraege?: DashboardAntraege;
  my_pending_proposals?: number;
  events?: DashboardEvent[];
}
